import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import RealtimeCamera from "./RealtimeCamera";
import SelfieCamera from "./SelfieCamera";
import UploadError from "../../reusable/UploadError";
import LivenessHelp from "./LivenessHelp";
import { IS_DEV } from "../../utils";
import { trackEvent } from "../../../utils/analytics";

function LivenessPrompt({ side, inputChange, gizmo, verification }) {
  const { t } = useTranslation();
  const [takePicture, setTakePicture] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingError, setUploadingError] = useState("");
  const [captured, setCaptured] = useState(null);
  const search = window.location.search;
  const params = new URLSearchParams(search);

  const biometricSetting = useMemo(() => {
    return verification?.settings.reduce(
      (acc, setting) => {
        if (setting.fieldName === "BIOMETRICS") {
          acc.biometricSetting = setting;
        }
        return acc;
      },
      { biometricSetting: null },
    ).biometricSetting;
  }, [verification.settings]);

  const openCamera = () => {
    trackEvent("liveness_capture_opened", {
      flowId: verification?.flowId,
      verificationId: verification?.verificationId,
      biometricType: biometricSetting?.value,
    });
    setTakePicture(true);
  };

  const onCapture = (input) => {
    setCaptured(input);
    validateImage(input);
  };

  const validateImage = (input) => {
    setUploading(true);

    let file = {};
    let field = {};
    if (input.type === "video") {
      const fileName = `${verification?.verificationId}-liveness.mp4`;
      file = { name: fileName, blob: input.value };
      field = {
        fileName: fileName,
        fieldName: side,
        value: fileName,
      };
    } else {
      const fileName = `${verification?.verificationId}-liveness.jpeg`;
      file = { name: fileName, blob: input.value };
      field = {
        fileName: fileName,
        fieldName: side,
        value: fileName,
      };
    }

    inputChange([
      {
        document: file,
        field: field,
      },
    ]);
    setTakePicture(false);
    setUploading(false);
  };

  const tryAgain = () => {
    setUploading(false);
    setTakePicture(false);
    setUploadingError("");
  };

  return (
    <div className="h-full w-full flex flex-col">
      {takePicture && biometricSetting?.value === "SELFIE_VIDEO" && (
        <RealtimeCamera
          uploading={uploading}
          onCapture={onCapture}
          debug={IS_DEV && params.get("debug") === "true"}
          flowId={verification?.flowId}
          verificationId={verification?.verificationId}
        />
      )}
      {takePicture && biometricSetting?.value === "SELFIE_PHOTO" && (
        <SelfieCamera
          uploading={uploading}
          onCapture={onCapture}
          clientId={params.get("clientId")}
          debug={IS_DEV && params.get("debug") === "true"}
          flowId={verification?.flowId}
          verificationId={verification?.verificationId}
        />
      )}
      {uploadingError && !takePicture && (
        <UploadError
          tryAgainFunc={tryAgain}
          imageSrc={captured}
          title={t("liveness.prompt.uploadFailedTitle")}
          desc={uploadingError}
        />
      )}
      {!takePicture && !uploadingError && !uploading && (
        <LivenessHelp openCamera={openCamera} biometricType={side} />
      )}
    </div>
  );
}

export default LivenessPrompt;
